import React from 'react';
import { Modal } from '@/components/ui';
import { formatKeyboardShortcut, type KeyboardShortcut } from '@/hooks/useKeyboardShortcuts';

interface KeyboardShortcutHelpProps {
  shortcuts: KeyboardShortcut[];
  isHelpModalOpen: boolean;
  setIsHelpModalOpen: (isOpen: boolean) => void;
}

export const KeyboardShortcutHelp: React.FC<KeyboardShortcutHelpProps> = ({ 
  shortcuts,
  isHelpModalOpen,
  setIsHelpModalOpen
}) => {

  // Group shortcuts by category
  const groupedShortcuts = shortcuts.reduce((acc, shortcut) => {
    if (!acc[shortcut.group]) {
      acc[shortcut.group] = [];
    }
    acc[shortcut.group].push(shortcut);
    return acc;
  }, {} as Record<string, KeyboardShortcut[]>);

  return (
    <Modal
      isOpen={isHelpModalOpen}
      onClose={() => setIsHelpModalOpen(false)}
      title="Keyboard Shortcuts"
      size="lg"
    >
      <div className="space-y-6">
        {/* Header */}
        <p className="text-body text-muted-foreground">
          Use these keyboard shortcuts to navigate and perform actions quickly.
        </p>

        {/* Shortcut Groups */}
        <div className="space-y-6">
          {Object.entries(groupedShortcuts).map(([group, groupShortcuts]) => (
            <div key={group}>
              {/* Group Title */}
              <h3 className="text-h4 text-foreground mb-3 font-semibold">
                {group}
              </h3>

              {/* Shortcuts List */}
              <div className="space-y-2">
                {groupShortcuts.map((shortcut, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between py-2 px-3 rounded-md
                             bg-muted
                             hover:bg-accent
                             transition-colors"
                  >
                    {/* Description */}
                    <span className="text-body text-foreground">
                      {shortcut.description}
                    </span>

                    {/* Keyboard Keys */}
                    <div className="flex items-center gap-1">
                      {formatKeyboardShortcut(shortcut)
                        .split(' + ')
                        .map((key, keyIndex, array) => (
                          <React.Fragment key={keyIndex}>
                            <kbd
                              className="px-2 py-1 text-small font-mono rounded
                                       bg-background
                                       border border-border
                                       text-foreground
                                       shadow-sm"
                            >
                              {key}
                            </kbd>
                            {keyIndex < array.length - 1 && (
                              <span className="text-muted-foreground/70">
                                +
                              </span>
                            )}
                          </React.Fragment>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer Tip */}
        <div className="pt-4 border-t border-border">
          <p className="text-small text-muted-foreground/70 text-center">
            Press <kbd className="px-2 py-0.5 mx-1 text-small font-mono rounded bg-muted">?</kbd> 
            {' '}anytime to toggle this help
          </p>
        </div>
      </div>
    </Modal>
  );
};

